// ── Control Plane Resolution Engine ──
// 9-pass deterministic evaluation engine.
// Produces fully resolved capability states from registry + context.

import type {
  CapabilityDefinition, ResolvedCapability, ResolutionContext,
  BehaviorMode, PassTraceEntry, ModeProfile,
} from "./types";
import { CAPABILITY_REGISTRY, getCapabilityById } from "./registry";
import { MODE_PROFILES } from "./profiles";

// ── Internal working state ──

interface WorkingCapability {
  def: CapabilityDefinition;
  enabled: boolean;
  mode: BehaviorMode;
  accessible: boolean;
  reason: string;
  trace: PassTraceEntry[];
}

function trace(w: WorkingCapability, pass: string, action: string, newEnabled: boolean, newMode: BehaviorMode): void {
  w.trace.push({
    pass,
    action,
    before: { enabled: w.enabled, mode: w.mode },
    after: { enabled: newEnabled, mode: newMode },
  });
  w.enabled = newEnabled;
  w.mode = newMode;
}

// ══════════════════════════════════════════════════════════
// RESOLUTION PASS ORDER CONTRACT (do not reorder):
//   1. Registry Load     — baseline from definitions
//   2. Environment       — hide internal_only in production
//   3. Tenant/Profile    — tenant-level licensing overrides
//   4. Role              — restrict access by user role
//   5. Dependency        — cascade disable from disabled parents
//   6. Conflict          — resolve mutually exclusive capabilities
//   7. User Override     — final per-user adjustments (POST-dependency)
//   8. UI Projection     — degrade/stub disabled capabilities
//   9. Behavior          — apply shadow mode logic
//
// KEY CONTRACT: User overrides (Pass 7) do NOT cascade because they
// run after dependency resolution (Pass 5). To cascade a disable,
// use a tenant profile override (Pass 3) instead.
// ══════════════════════════════════════════════════════════

// ── Pass 1: Registry Load ──
function registryLoadPass(): Map<string, WorkingCapability> {
  const map = new Map<string, WorkingCapability>();
  CAPABILITY_REGISTRY.forEach(def => {
    map.set(def.id, {
      def,
      enabled: def.enabled,
      mode: def.behaviorMode,
      accessible: true,
      reason: "Registry default",
      trace: [],
    });
  });
  return map;
}

// ── Pass 2: Environment ──
function environmentPass(caps: Map<string, WorkingCapability>, ctx: ResolutionContext): void {
  caps.forEach(w => {
    if (w.def.behaviorMode === "internal_only" && ctx.environment === "production") {
      trace(w, "Environment", `Internal-only capability hidden in ${ctx.environment}`, false, "hidden");
      w.reason = `Disabled: internal_only not allowed in ${ctx.environment}`;
    }
    if (w.def.tier === "internal" && ctx.environment === "production") {
      trace(w, "Environment", `Internal tier hidden in production`, false, "hidden");
      w.reason = "Disabled: internal tier in production";
    }
  });
}

// ── Pass 3: Tenant/Account ──
function tenantPass(caps: Map<string, WorkingCapability>, ctx: ResolutionContext): void {
  // Mode profile application (tenant-level licensing)
  const profile = MODE_PROFILES.find(p => p.id === ctx.modeProfile);
  if (!profile) return;

  Object.entries(profile.overrides).forEach(([capId, override]) => {
    const w = caps.get(capId);
    if (!w) return;
    trace(w, "Tenant/Profile", `Profile "${profile.label}" sets ${capId}`, override.enabled, override.behaviorMode);
    w.reason = `Set by profile: ${profile.label}`;
  });
}

// ── Pass 4: Role/User ──
function rolePass(caps: Map<string, WorkingCapability>, ctx: ResolutionContext): void {
  caps.forEach(w => {
    if (w.def.rolesAllowed.length > 0 && !w.def.rolesAllowed.includes(ctx.userRole)) {
      w.accessible = false;
      trace(w, "Role", `Role "${ctx.userRole}" not in allowed list`, w.enabled, "hidden");
      w.reason = `Not accessible for role: ${ctx.userRole}`;
    }
  });
}

// ── Pass 5: Dependency Resolution ──
function dependencyPass(caps: Map<string, WorkingCapability>): void {
  // Iterative resolution — max 10 passes to handle chains
  for (let i = 0; i < 10; i++) {
    let changed = false;
    caps.forEach(w => {
      if (!w.enabled) return;
      for (const depId of w.def.dependencies) {
        const dep = caps.get(depId);
        if (dep && !dep.enabled) {
          trace(w, "Dependency", `Dependency "${depId}" is disabled`, false, w.mode);
          w.reason = `Disabled: dependency "${depId}" is off`;
          changed = true;
        }
      }
    });
    if (!changed) break;
  }
}

// ── Pass 6: Conflict Resolution ──
function conflictPass(caps: Map<string, WorkingCapability>): void {
  caps.forEach(w => {
    if (!w.enabled) return;
    for (const conflictId of w.def.conflicts) {
      const conflict = caps.get(conflictId);
      if (conflict && conflict.enabled) {
        // Lower-tier capability loses
        const tierOrder = { core: 0, advanced: 1, enterprise: 2, internal: 3 };
        const wTier = tierOrder[w.def.tier] ?? 0;
        const cTier = tierOrder[conflict.def.tier] ?? 0;
        if (wTier <= cTier) {
          trace(w, "Conflict", `Conflicts with "${conflictId}" (higher/equal tier wins)`, false, w.mode);
          w.reason = `Disabled: conflicts with ${conflictId}`;
        }
      }
    }
  });
}

// ── Pass 7: User Overrides ──
function userOverridePass(caps: Map<string, WorkingCapability>, ctx: ResolutionContext): void {
  Object.entries(ctx.overrides).forEach(([capId, override]) => {
    const w = caps.get(capId);
    if (!w) return;
    const newEnabled = override.enabled ?? w.enabled;
    const newMode = override.behaviorMode ?? w.mode;
    trace(w, "UserOverride", `Manual override applied`, newEnabled, newMode);
    w.reason = `User override`;
  });
}

// ── Pass 8: UI Projection ──
function uiProjectionPass(caps: Map<string, WorkingCapability>): void {
  caps.forEach(w => {
    if (!w.enabled || !w.accessible) {
      const fb = w.def.fallbackBehavior;
      if (fb.onDisable === "degrade_to_readonly" && w.accessible) {
        trace(w, "UIProjection", "Degraded to read-only", w.enabled, "read_only");
      } else if (fb.onDisable === "replace_with_stub") {
        trace(w, "UIProjection", "Replaced with stub", w.enabled, "hidden");
      }
      // "remove_section" → already hidden, no trace needed
    }
  });
}

// ── Pass 9: Behavior Projection + Snapshot ──
function behaviorProjectionPass(caps: Map<string, WorkingCapability>): void {
  caps.forEach(w => {
    if (w.mode === "shadow") {
      // Shadow mode: logic runs but UI is hidden
      trace(w, "BehaviorProjection", "Shadow mode: logic active, UI suppressed", true, "shadow");
    }
  });
}

// ── Main Resolution Function ──

export function resolveCapabilities(ctx: ResolutionContext): ResolvedCapability[] {
  // Pass 1
  const caps = registryLoadPass();
  // Pass 2
  environmentPass(caps, ctx);
  // Pass 3
  tenantPass(caps, ctx);
  // Pass 4
  rolePass(caps, ctx);
  // Pass 5
  dependencyPass(caps);
  // Pass 6
  conflictPass(caps);
  // Pass 7
  userOverridePass(caps, ctx);
  // Pass 8
  uiProjectionPass(caps);
  // Pass 9
  behaviorProjectionPass(caps);

  return [...caps.values()].map(w => ({
    id: w.def.id,
    label: w.def.label,
    category: w.def.category,
    tier: w.def.tier,
    effectiveMode: w.mode,
    enabled: w.enabled,
    accessible: w.accessible,
    reason: w.reason,
    passTrace: w.trace,
    influence: w.def.influence,
    fallbackBehavior: w.def.fallbackBehavior,
  }));
}

// ── Decision Helpers ──

export function canUseCapability(resolved: ResolvedCapability[], capabilityId: string): boolean {
  const cap = resolved.find(c => c.id === capabilityId);
  return cap ? cap.enabled && cap.accessible && cap.effectiveMode !== "hidden" : false;
}

export function getCapabilityMode(resolved: ResolvedCapability[], capabilityId: string): BehaviorMode {
  const cap = resolved.find(c => c.id === capabilityId);
  return cap?.effectiveMode ?? "hidden";
}

export function getCapabilityInfluence(resolved: ResolvedCapability[], capabilityId: string) {
  const cap = resolved.find(c => c.id === capabilityId);
  return cap?.influence ?? { ui: false, intelligence: false, recommendations: false, financials: false };
}

export function getEffectiveConfig(resolved: ResolvedCapability[]): Record<string, { enabled: boolean; mode: BehaviorMode }> {
  const config: Record<string, { enabled: boolean; mode: BehaviorMode }> = {};
  resolved.forEach(c => { config[c.id] = { enabled: c.enabled, mode: c.effectiveMode }; });
  return config;
}

// ── Dependency Graph ──

export interface DependencyNode {
  id: string;
  label: string;
  dependencies: string[];
  dependents: string[];
  enabled: boolean;
}

export function buildDependencyGraph(resolved: ResolvedCapability[]): DependencyNode[] {
  const nodes: DependencyNode[] = resolved.map(r => {
    const def = getCapabilityById(r.id);
    return {
      id: r.id,
      label: r.label,
      dependencies: def?.dependencies ?? [],
      dependents: [],
      enabled: r.enabled,
    };
  });
  nodes.forEach(node => {
    node.dependencies.forEach(depId => {
      const dep = nodes.find(n => n.id === depId);
      if (dep) dep.dependents.push(node.id);
    });
  });
  return nodes;
}

// ══════════════════════════════════════════
// ── Explainability Engine ──
// ══════════════════════════════════════════

import type {
  DecisionSummary, ImpactPreview, ChangeRequest, SimulationResult,
  SimulationDiffEntry, ChangeRiskLevel, ManagedUser, ManagedTenant,
} from "./types";

export function explainDecision(cap: ResolvedCapability): DecisionSummary {
  const trace = cap.passTrace;
  const lastMutation = [...trace].reverse().find(t =>
    t.before.enabled !== t.after.enabled || t.before.mode !== t.after.mode
  );
  const primaryPass = lastMutation?.pass ?? "Registry";

  const depIssues = trace.filter(t => t.pass === "Dependency" && !t.after.enabled).map(t => t.action);
  const conflictIssues = trace.filter(t => t.pass === "Conflict" && !t.after.enabled).map(t => t.action);
  const roleTrace = trace.find(t => t.pass === "Role");
  const overrideTrace = trace.find(t => t.pass === "UserOverride");
  const tenantTrace = trace.find(t => t.pass === "Tenant/Profile");

  let explanation = "";
  if (!cap.enabled) {
    if (depIssues.length > 0) {
      explanation = `Disabled because ${depIssues.length === 1 ? "a required dependency is" : "required dependencies are"} turned off: ${depIssues.join("; ")}.`;
    } else if (conflictIssues.length > 0) {
      explanation = `Disabled due to conflict with a higher-priority capability.`;
    } else if (roleTrace && !cap.accessible) {
      explanation = `Your current role does not have access to this capability.`;
    } else if (tenantTrace) {
      explanation = `Disabled by the active mode profile applied to this tenant.`;
    } else {
      explanation = `${cap.reason}.`;
    }
  } else if (cap.effectiveMode === "read_only") {
    explanation = `Active but in read-only mode. ${cap.reason}.`;
  } else if (cap.effectiveMode === "shadow") {
    explanation = `Running in shadow mode — logic executes but UI is hidden.`;
  } else {
    explanation = `Fully active. Resolved through ${trace.length > 0 ? trace.length + " passes" : "registry defaults"} with no restrictions.`;
  }

  return {
    capabilityId: cap.id,
    finalState: cap.effectiveMode,
    finalReason: cap.reason,
    primaryDecidingPass: primaryPass,
    overriddenBy: overrideTrace ? "User override" : undefined,
    dependencyIssues: depIssues,
    conflictIssues: conflictIssues,
    roleRestriction: roleTrace && !cap.accessible ? `Role not in allowed list` : null,
    tenantInfluence: tenantTrace ? tenantTrace.action : null,
    userOverride: overrideTrace ? overrideTrace.action : null,
    explanation,
  };
}

// ══════════════════════════════════════════
// ── Impact Engine ──
// ══════════════════════════════════════════

export function computeImpact(
  change: ChangeRequest,
  currentResolved: ResolvedCapability[],
  simulatedResolved: ResolvedCapability[],
  users: ManagedUser[],
  tenants: ManagedTenant[],
): ImpactPreview {
  const diff = computeDiff(currentResolved, simulatedResolved);

  const affectedCapIds = new Set(diff.map(d => d.capabilityId));
  const affectedDefs = CAPABILITY_REGISTRY.filter(c => affectedCapIds.has(c.id));
  const affectedRoutes = [...new Set(affectedDefs.flatMap(d => d.uiSurfaces))];
  const affectedModules = [...new Set(affectedDefs.map(d => d.category))];

  const behavioralChanges = diff.map(d => {
    const def = getCapabilityById(d.capabilityId);
    return `${def?.label ?? d.capabilityId}: ${d.field} changed from "${d.before}" to "${d.after}"`;
  });

  const criticalWarnings: string[] = [];
  const disabledCaps = diff.filter(d => d.field === "enabled" && d.after === "false");
  const financialCaps = disabledCaps.filter(d => {
    const def = getCapabilityById(d.capabilityId);
    return def?.influence.financials;
  });
  if (financialCaps.length > 0) {
    criticalWarnings.push(`${financialCaps.length} financial capability(s) will be disabled`);
  }
  const coreCaps = disabledCaps.filter(d => {
    const def = getCapabilityById(d.capabilityId);
    return def?.tier === "core";
  });
  if (coreCaps.length > 0) {
    criticalWarnings.push(`${coreCaps.length} core capability(s) will be disabled`);
  }

  // Estimate affected users
  let affectedUserCount = 0;
  let affectedTenantCount = 0;
  if (change.type === "environment_change") {
    affectedUserCount = users.length;
    affectedTenantCount = tenants.length;
  } else if (change.type === "user_role_change" || change.type === "user_status_change") {
    affectedUserCount = 1;
  } else if (change.type === "tenant_profile_change" || change.type === "tenant_capability_override" || change.type === "tenant_status_change") {
    const tenantUsers = users.filter(u => u.tenantId === change.targetId);
    affectedUserCount = tenantUsers.length;
    affectedTenantCount = 1;
  } else {
    // capability override — affects all users who had access
    const capDef = getCapabilityById(change.targetId);
    affectedUserCount = capDef ? users.filter(u => capDef.rolesAllowed.includes(u.role)).length : 0;
  }

  const riskLevel = classifyRisk(diff, criticalWarnings, affectedUserCount, affectedModules);

  return {
    affectedUsers: affectedUserCount,
    affectedTenants: affectedTenantCount,
    affectedCapabilities: affectedCapIds.size,
    affectedRoutes,
    affectedModules,
    criticalWarnings,
    behavioralChanges,
    riskLevel,
  };
}

function classifyRisk(
  diff: SimulationDiffEntry[],
  warnings: string[],
  affectedUsers: number,
  affectedModules: string[],
): ChangeRiskLevel {
  if (warnings.length > 0 || affectedUsers > 10 || affectedModules.includes("finance")) return "critical";
  if (diff.length > 5 || affectedUsers > 5) return "high";
  if (diff.length > 2 || affectedUsers > 1) return "medium";
  return "low";
}

// ══════════════════════════════════════════
// ── Simulation / Dry-Run Engine ──
// ══════════════════════════════════════════

export function simulateChange(
  change: ChangeRequest,
  currentCtx: ResolutionContext,
  currentResolved: ResolvedCapability[],
  users: ManagedUser[],
  tenants: ManagedTenant[],
): SimulationResult {
  // Clone context and apply hypothetical change
  const simCtx: ResolutionContext = JSON.parse(JSON.stringify(currentCtx));

  switch (change.type) {
    case "capability_override": {
      const { enabled, behaviorMode } = change.payload as { enabled: boolean; behaviorMode: BehaviorMode };
      simCtx.overrides = { ...simCtx.overrides, [change.targetId]: { enabled, behaviorMode } };
      break;
    }
    case "environment_change": {
      simCtx.environment = change.payload.environment as ResolutionContext["environment"];
      break;
    }
    case "tenant_profile_change": {
      simCtx.modeProfile = change.payload.modeProfile as ResolutionContext["modeProfile"];
      break;
    }
    case "user_role_change": {
      simCtx.userRole = change.payload.role as ResolutionContext["userRole"];
      break;
    }
    default:
      break;
  }

  const simulatedResolved = resolveCapabilities(simCtx);
  const diff = computeDiff(currentResolved, simulatedResolved);
  const impactPreview = computeImpact(change, currentResolved, simulatedResolved, users, tenants);

  return {
    changeRequest: change,
    currentResolved,
    simulatedResolved,
    diff,
    impactPreview,
    simulatedAt: new Date().toISOString(),
  };
}

function computeDiff(current: ResolvedCapability[], simulated: ResolvedCapability[]): SimulationDiffEntry[] {
  const diffs: SimulationDiffEntry[] = [];
  current.forEach(c => {
    const s = simulated.find(r => r.id === c.id);
    if (!s) return;
    if (c.enabled !== s.enabled) {
      diffs.push({ capabilityId: c.id, label: c.label, field: "enabled", before: String(c.enabled), after: String(s.enabled) });
    }
    if (c.effectiveMode !== s.effectiveMode) {
      diffs.push({ capabilityId: c.id, label: c.label, field: "effectiveMode", before: c.effectiveMode, after: s.effectiveMode });
    }
    if (c.accessible !== s.accessible) {
      diffs.push({ capabilityId: c.id, label: c.label, field: "accessible", before: String(c.accessible), after: String(s.accessible) });
    }
  });
  return diffs;
}
